import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SCENES, useScene } from '../../context/SceneContext';

/**
 * GlobalCanvas — fixed full-viewport background that crossfades between
 * pre-generated 'Midnight Blueprint' scene images.
 *
 * Layered above the page bg color but below all page content (z = 0).
 * Includes a subtle vignette + grain to keep the editorial feel.
 */
export default function GlobalCanvas() {
  const { sceneIndex, scenes } = useScene();
  const scene = scenes[sceneIndex];

  // Lazily preload scenes only when the browser is idle, and only the
  // current scene's neighbours — avoids blocking page load with ~800KB of
  // images that are not yet on screen.
  useEffect(() => {
    const ric = window.requestIdleCallback || ((cb) => setTimeout(cb, 1500));
    const handle = ric(() => {
      const order = [sceneIndex, (sceneIndex + 1) % SCENES.length, (sceneIndex - 1 + SCENES.length) % SCENES.length];
      const seen = new Set();
      const queue = [...order, ...SCENES.map((_, i) => i)].filter((i) => !seen.has(i) && (seen.add(i), true));
      queue.forEach((i, k) => {
        setTimeout(() => {
          const img = new Image();
          img.src = SCENES[i].src;
        }, k * 120); // stagger so each load doesn't compete
      });
    }, { timeout: 3000 });
    return () => {
      if (window.cancelIdleCallback) window.cancelIdleCallback(handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="fixed inset-0 pointer-events-none z-0 overflow-hidden"
      data-testid="global-scene-canvas"
      aria-hidden="true"
    >
      {/* Solid charcoal under-layer */}
      <div className="absolute inset-0 bg-[#0E1116]" />

      {/* Crossfading scene image */}
      <AnimatePresence mode="sync">
        <motion.div
          key={scene.id}
          className="absolute inset-0"
          initial={{ opacity: 0, scale: 1.04, filter: 'blur(14px)' }}
          animate={{ opacity: 0.55, scale: 1, filter: 'blur(0px)' }}
          exit={{ opacity: 0, scale: 0.98, filter: 'blur(8px)' }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          style={{
            backgroundImage: `url("${scene.src}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />
      </AnimatePresence>

      {/* Vignette to favor copy at edges */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 0%, rgba(14,17,22,0.55) 60%, rgba(14,17,22,0.92) 100%)',
        }}
      />

      {/* Subtle blueprint grid overlay locked to scene-accent */}
      <div
        className="absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            'linear-gradient(var(--scene-accent, #3B82F6) 1px, transparent 1px), linear-gradient(90deg, var(--scene-accent, #3B82F6) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
        }}
      />

      {/* Grain */}
      <div
        className="absolute inset-0 opacity-[0.06] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />
    </div>
  );
}
